import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PlayerRef } from '@remotion/player';
import { MusicNote, PauseFill, PlayArrowFill } from '@crabs-ai/web-icons';
import { cn, toast } from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';
import { motion } from 'motion/react';
import type {BgmCatalogItem as SharedAssetCatalogItem} from '@waka/compat/bgm-catalog';
import {readAudioDuration} from '@waka/compat/audio-duration';
import {readBgmFile} from '@waka/compat/bgm-file';
import {
  useCurrentStateAsRef,
  useWriteContext,
} from '../../../../utils/use-context';
import {
  DtoContentType,
  DtoExtension,
  DtoProjectType,
} from '@/shared/constants';
import {
  BGM_DRAG_TYPE,
  BgmDragData,
  setCurrentBgmDragData,
} from './bgm-drag-data';
import { convertMillisecondsToTimeFormat } from '@/shared';
import { addBgmAssetToTimeline } from './add-bgm-asset-to-timeline';

const PROPERTY_AUDIO_PLAY_EVENT = 'property-audio-item-play';
const PROPERTY_AUDIO_STOP_EVENT = 'property-audio-item-stop';

const EXTENSION_MAP: Record<string, DtoExtension> = {
  mp3: DtoExtension.mp3,
  wav: DtoExtension.wav,
  aac: DtoExtension.aac,
};

interface BgmItemProps {
  item: SharedAssetCatalogItem;
  playerRef: React.RefObject<PlayerRef | null>;
}

export default function BgmItem({ item, playerRef }: BgmItemProps) {
  const t = useTranslations();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [currentTimeInSeconds, setCurrentTimeInSeconds] = useState(0);
  const [loadedDurationInSeconds, setLoadedDurationInSeconds] = useState(
    item.duration ? item.duration / 1000 : 0,
  );
  const audioRef = useRef<HTMLAudioElement>(null);
  const lifetime=useRef<AbortController|null>(null);
  const adding=useRef(false);
  const preparing=useRef<Promise<number>|null>(null);
  const previewActive=useRef(false),previewGeneration=useRef(0);
  const [isAdding,setIsAdding]=useState(false);
  useEffect(()=>{const control=new AbortController(),audio=audioRef.current;lifetime.current=control;adding.current=false;setIsAdding(false);setLoadedDurationInSeconds(item.duration?item.duration/1000:0);
    itemIdRef.current=item.contentId;
    return()=>{control.abort();preparing.current=null;previewActive.current=false;previewGeneration.current++;audio?.pause();};},[item.contentId,item.publicUrl,item.duration]);
  const itemIdRef = useRef(item.contentId);

  const timelineWriteContext = useWriteContext();
  const stateAsRef = useCurrentStateAsRef();

  const isUnavailable = !item.publicUrl;

  const prepareDuration = useCallback(() => {
    if (preparing.current) return preparing.current;
    const audio = audioRef.current, control = lifetime.current;
    if (!audio || !control) return Promise.reject(new Error('배경음 준비 중입니다.'));
    const pending = readAudioDuration(audio, control.signal).then(duration => {
      if (lifetime.current === control && !control.signal.aborted) setLoadedDurationInSeconds(duration);
      return duration;
    }).finally(() => { if (preparing.current === pending) preparing.current = null; });
    preparing.current = pending;
    return pending;
  }, []);

  const warmDuration = useCallback(() => {
    // Only the approached card is prepared, never the entire music catalog.
    if (!isUnavailable && loadedDurationInSeconds <= 0) void prepareDuration().catch(() => {});
  }, [isUnavailable, loadedDurationInSeconds, prepareDuration]);

  // 미리듣기 정지와 상태 초기화를 한 곳에 모아
  // click-add / drag-start / 다른 카드 재생 시작 시 동일하게 재사용한다.
  const stopPreview = useCallback((resetToStart = true) => {
    previewActive.current=false;previewGeneration.current++;
    if (audioRef.current) {
      audioRef.current.pause();

      if (resetToStart) {
        audioRef.current.currentTime = 0;
      }
    }

    if (resetToStart) {
      setCurrentTimeInSeconds(0);
    }

    setIsPlaying(false);
  }, []);

  useEffect(() => {
    const handleOtherPlay = (e: CustomEvent<number|string>) => {
      if (e.detail !== itemIdRef.current) {
        stopPreview();
      }
    };

    const handleStop = () => {
      stopPreview();
    };

    window.addEventListener(
      PROPERTY_AUDIO_PLAY_EVENT,
      handleOtherPlay as EventListener,
    );
    window.addEventListener(PROPERTY_AUDIO_STOP_EVENT, handleStop);

    return () => {
      window.removeEventListener(
        PROPERTY_AUDIO_PLAY_EVENT,
        handleOtherPlay as EventListener,
      );
      window.removeEventListener(PROPERTY_AUDIO_STOP_EVENT, handleStop);
    };
  }, [isPlaying, stopPreview]);

  const handleEnded = useCallback(() => {
    stopPreview();
  }, [stopPreview]);

  const handleLoadedMetadata = useCallback(() => {
    const duration = audioRef.current?.duration;

    if (
      typeof duration === 'number' &&
      Number.isFinite(duration) &&
      duration > 0
    ) {
      setLoadedDurationInSeconds(duration);
      return;
    }

    setLoadedDurationInSeconds(item.duration ? item.duration / 1000 : 0);
  }, [item.duration]);

  const handleTimeUpdate = useCallback(() => {
    if (!audioRef.current) {
      return;
    }

    setCurrentTimeInSeconds(audioRef.current.currentTime);
  }, []);

  const handlePlayToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (isUnavailable || !audioRef.current) {
        return;
      }

      if (previewActive.current) {
        stopPreview(false);
        return;
      }

      window.dispatchEvent(
        new CustomEvent(PROPERTY_AUDIO_PLAY_EVENT, { detail: item.contentId }),
      );
      previewActive.current=true;const generation=++previewGeneration.current;
      // play 버튼으로 다시 시작할 때는 항상 처음부터 재생한다.
      audioRef.current.currentTime = 0;
      setCurrentTimeInSeconds(0);
      audioRef.current
        .play()
        .then(() => {if(generation===previewGeneration.current)setIsPlaying(true);})
        .catch(() => {if(generation===previewGeneration.current){previewActive.current=false;setIsPlaying(false);}});
    },
    [isPlaying, isUnavailable, item.contentId, stopPreview],
  );

  const handleSeek = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      if (isUnavailable || !audioRef.current || loadedDurationInSeconds <= 0) {
        return;
      }

      const rect = e.currentTarget.getBoundingClientRect();
      const ratio = Math.min(
        1,
        Math.max(0, (e.clientX - rect.left) / rect.width),
      );
      const nextTime = loadedDurationInSeconds * ratio;

      audioRef.current.currentTime = nextTime;
      setCurrentTimeInSeconds(nextTime);
    },
    [isUnavailable, loadedDurationInSeconds],
  );

  const getExtension = useCallback(() => {
    if (!item.publicUrl) {
      return {
        stringExtension: 'mp3',
        dtoExtension: DtoExtension.mp3,
      };
    }

    const pathname = new URL(item.publicUrl).pathname;
    const stringExtension = pathname.split('.').pop()?.toLowerCase() ?? 'mp3';

    return {
      stringExtension,
      dtoExtension: EXTENSION_MAP[stringExtension] ?? DtoExtension.mp3,
    };
  }, [item.publicUrl]);

  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      if (isUnavailable || !item.publicUrl) {
        e.preventDefault();
        return;
      }

      // 타임라인 추가 액션이 시작되면 preview 잔존 재생을 바로 끊는다.
      stopPreview();
      window.dispatchEvent(new CustomEvent(PROPERTY_AUDIO_STOP_EVENT));
      setCurrentBgmDragData(null);

      const { dtoExtension } = getExtension();

      const dragData: BgmDragData = {
        itemId: item.contentId,
        name: item.name,
        publicUrl: item.publicUrl,
        contentType: DtoContentType.audio,
        duration: loadedDurationInSeconds>0?loadedDurationInSeconds*1000:null,
        extension: dtoExtension,
        wakaBgmSource:item.wakaBgmSource,
      };

      e.dataTransfer.setData(BGM_DRAG_TYPE, JSON.stringify(dragData));
      e.dataTransfer.effectAllowed = 'copy';
      setIsDragging(true);
      setCurrentBgmDragData(dragData);

      const dragElement = document.createElement('div');
      dragElement.textContent = item.name;
      dragElement.style.cssText = `
        position: absolute; top: -9999px; left: -9999px;
        padding: 8px 12px; background: rgba(0, 0, 0, 0.8);
        color: white; font-size: 12px; font-weight: 500;
        border-radius: 6px; white-space: nowrap;
        max-width: 200px; overflow: hidden; text-overflow: ellipsis;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      `;
      document.body.appendChild(dragElement);
      e.dataTransfer.setDragImage(dragElement, 0, 0);

      requestAnimationFrame(() => {
        document.body.removeChild(dragElement);
      });
    },
    [
      getExtension,
      isUnavailable,
      item.contentId,
      item.duration,
      item.name,
      item.publicUrl,
      loadedDurationInSeconds,
      item.wakaBgmSource,
      stopPreview,
      prepareDuration,
    ],
  );

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setCurrentBgmDragData(null);
  }, []);

  const handleClick = useCallback(async () => {
    if (isUnavailable || !item.publicUrl || adding.current || !audioRef.current || !lifetime.current) {
      return;
    }

    const control=lifetime.current;adding.current=true;setIsAdding(true);
    const initial=stateAsRef.current.undoableState;
    const insertionFrame=playerRef.current?.getCurrentFrame()??0;
    try {
      // click-add도 drag와 동일하게 preview가 남지 않게 정지부터 수행한다.
      stopPreview();
      window.dispatchEvent(new CustomEvent(PROPERTY_AUDIO_STOP_EVENT));
      const duration=await prepareDuration();
      if(control.signal.aborted)return;setLoadedDurationInSeconds(duration);
      const blob = await readBgmFile(item.publicUrl,item.wakaBgmSource,control.signal);
      if(control.signal.aborted||stateAsRef.current.undoableState!==initial)throw new Error('편집 내용이 바뀌어 추가하지 않았습니다.');
      const state = stateAsRef.current;
      const { stringExtension } = getExtension();

      await addBgmAssetToTimeline({
        file: blob,
        contentId: String(item.contentId),
        filename: `${item.name}.${stringExtension}`,
        contentPublicUri: item.publicUrl,
        durationInMilliseconds: duration*1000,
        wakaBgmSource:item.wakaBgmSource,
        timelineWriteContext,
        playerRef,
        fps: state.undoableState.fps,
        compositionWidth: state.undoableState.compositionWidth,
        compositionHeight: state.undoableState.compositionHeight,
        tracks: state.undoableState.tracks,
        projectType: DtoProjectType.spotlight,
        extension: stringExtension,
        timelineStartFrame: insertionFrame,
        placement: {
          type: 'new',
          insertAtIndex: state.undoableState.tracks.length,
        },
      });

      toast({
        type: 'success',
        description: t('Editor.audio-panel.bgm-add-success'),
        toastOptions: { position: 'top-center' },
      });
    } catch (error) {
      if(control.signal.aborted)return;
      console.error('BGM add failed:', error);
      toast({
        type: 'error',
        description: t('Editor.audio-panel.bgm-add-fail'),
        toastOptions: { position: 'top-center' },
      });
    }finally{if(lifetime.current===control){adding.current=false;if(!control.signal.aborted)setIsAdding(false);}}
  }, [
    getExtension,
    isUnavailable,
    item.contentId,
    item.duration,
    item.name,
    item.publicUrl,
    loadedDurationInSeconds,
    item.wakaBgmSource,
    playerRef,
    stateAsRef,
    t,
    timelineWriteContext,
    stopPreview,
    prepareDuration,
  ]);

  const durationLabel = useMemo(() => {
    const durationInMilliseconds = loadedDurationInSeconds * 1000;

    if (!durationInMilliseconds) {
      return '-';
    }

    return convertMillisecondsToTimeFormat(durationInMilliseconds);
  }, [loadedDurationInSeconds]);

  const progressPercentage = useMemo(() => {
    if (loadedDurationInSeconds <= 0) {
      return 0;
    }

    return Math.min(
      (currentTimeInSeconds / loadedDurationInSeconds) * 100,
      100,
    );
  }, [currentTimeInSeconds, loadedDurationInSeconds]);

  return (
    <div
      draggable={!isUnavailable}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      onMouseEnter={warmDuration}
      onFocus={warmDuration}
      className={cn(
        'flex flex-col rounded-sm px-sm py-xs transition-colors',
        isUnavailable
          ? 'cursor-not-allowed bg-sys-style-neutral-lightest opacity-60'
          : 'cursor-grab bg-sys-style-neutral-lightest hover:bg-sys-style-neutral-light active:cursor-grabbing',
        isDragging && 'opacity-50',
      )}
    >
      <div className="flex items-center gap-xs">
        <MusicNote className="size-md shrink-0 [&_path]:fill-sys-cont-neutral-default" />

        <div className="min-w-0 flex-1">
          <div className="truncate text-comp-moderate-medium text-sys-cont-neutral-default">
            {item.name}
            {isAdding&&<span> · 원본 확인 중…</span>}
          </div>
        </div>

        <button
          type="button"
          onClick={handlePlayToggle}
          aria-label={`${item.name} ${isPlaying?'미리 듣기 정지':'미리 듣기'}`}
          disabled={isUnavailable}
          className="flex size-lg items-center justify-center rounded-full transition-colors hover:bg-sys-style-neutral-default disabled:cursor-not-allowed disabled:hover:bg-transparent"
        >
          {isPlaying ? (
            <PauseFill className="size-md [&_path]:fill-sys-cont-primary-default" />
          ) : (
            <PlayArrowFill className="size-md [&_path]:fill-sys-cont-neutral-default" />
          )}
        </button>
        <button type="button" aria-label={`${item.name} 타임라인에 추가`} disabled={isUnavailable||isAdding}
          onClick={event=>{event.stopPropagation();void handleClick();}}
          className="flex size-lg items-center justify-center rounded-full hover:bg-sys-style-neutral-default disabled:opacity-50">+</button>

        <span className="text-sys-cont-neutral-tertiary min-w-[56px] text-right text-comp-accent-small">
          {durationLabel}
        </span>
      </div>

      <motion.div
        initial={false}
        animate={
          isPlaying
            ? {
                opacity: 1,
                height: 12,
                marginTop: 4,
              }
            : {
                opacity: 0,
                height: 0,
                marginTop: 0,
              }
        }
        // wrapper를 항상 유지한 채 접히게 해서
        // bar가 사라질 때 레이아웃이 '툭' 끊기는 느낌을 줄인다.
        transition={{ duration: 0.22, ease: 'easeOut' }}
        className="overflow-hidden"
      >
        <button
          type="button"
          onClick={handleSeek}
          disabled={isUnavailable || !isPlaying}
          aria-label={t('Editor.audio-panel.bgm-seek')}
          className="flex w-full cursor-pointer items-center disabled:cursor-not-allowed"
        >
          <div className="h-1 w-full overflow-hidden rounded-full bg-sys-style-neutral-default">
            <div
              className="h-full rounded-full bg-sys-cont-primary-default"
              style={{
                // width transition 대신 scaleX를 써서 진행률 업데이트 시 버벅임을 줄인다.
                transform: `scaleX(${progressPercentage / 100})`,
                transformOrigin: 'left center',
              }}
            />
          </div>
        </button>
      </motion.div>

      <audio
        ref={audioRef}
        src={item.publicUrl ?? ''}
        preload="none"
        onEnded={handleEnded}
        onLoadedMetadata={handleLoadedMetadata}
        onTimeUpdate={handleTimeUpdate}
      />
    </div>
  );
}
